import { apiUrl } from '@algoan/dashboard/env';
import { AsyncThunk, Dispatch } from '@algoan/dashboard/store/store';
import {
  Customer,
  Dashboard,
  PaginatedResources,
  State,
  StateFamily,
  InvitationResponse,
  AutomaticSendType,
  CustomerInformationData,
  Language,
  PatchCustomerPersonalDetailsDto,
  UserJourneyState,
  NotificationVariant,
} from '@algoan/dashboard/model';
import {
  fetchNextCustomerFailure,
  fetchNextCustomerStart,
  fetchNextCustomerSuccess,
  fetchOneCustomerFailure,
  fetchOneCustomerStart,
  fetchOneCustomerSuccess,
  resetCustomerList,
  sortCustomersStart,
  sortCustomersSuccess,
  updateCustomerDashboardState,
  CustomersFilter,
  filterCustomersSuccess,
  filterCustomersStart,
  setLoanDecision,
} from '@algoan/dashboard/store/slice';
import {
  paginationSelector,
  sortSelector,
  filterSelector,
  filterColumnsSelector,
} from '@algoan/dashboard/store/selectors';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { buildUrl, escapeRegexReservedCharacters, getMongoSortValue } from '@algoan/dashboard/utils';
import { authRequest } from './auth-thunks';
import { notify } from './app-state-thunks';
import { customerMessages } from '@algoan/dashboard/store/messages';
import axios, { CancelTokenSource } from 'axios';

const perPage = 50;

export const interruptedFlowUserJourneyStates = [
  UserJourneyState.STARTED,
  UserJourneyState.QR_CODE_SCANNED,
  UserJourneyState.BANK_SELECTED,
];

export const bankConnectionFailedUserJourneyStates = [
  UserJourneyState.AGGREGATION_STARTED,
  UserJourneyState.AGGREGATION_FINISHED_SUCCESS,
  UserJourneyState.FINISHED_SUCCESS,
];

export const hasErrorUserJourneyStates = [
  UserJourneyState.AGGREGATION_FINISHED_ERROR,
  UserJourneyState.FINISHED_ERROR,
  UserJourneyState.ANALYSIS_ERROR,
];

export let indexPageCancellationToken: CancelTokenSource | undefined;

const getUserJourneyStateFilter = (
  stateFamilyArray: StateFamily[] | undefined,
): { $in: UserJourneyState[] } | undefined => {
  if (stateFamilyArray === undefined || stateFamilyArray.length === 3) {
    return undefined;
  }

  const hasFlowInterrupted = stateFamilyArray.includes(StateFamily.FLOW_STARTED);
  const hasBankConnectionFailed = stateFamilyArray.includes(StateFamily.AGGREGATION_STARTED);
  const hasError = stateFamilyArray.includes(StateFamily.IN_ERROR);

  return {
    $in: [
      ...(hasFlowInterrupted ? interruptedFlowUserJourneyStates : []),
      ...(hasBankConnectionFailed ? bankConnectionFailedUserJourneyStates : []),
      ...(hasError ? hasErrorUserJourneyStates : []),
    ],
  };
};

export const fetchOneCustomer =
  (customerId: string): AsyncThunk<Customer | undefined> =>
  async (dispatch) => {
    const customerUrl = `${apiUrl}/v2/customers/${customerId}`;

    try {
      dispatch(fetchOneCustomerStart());

      const { data: customer } = await dispatch(authRequest<Customer>('get', customerUrl));

      if (!customer) {
        dispatch(fetchOneCustomerFailure());

        return undefined;
      }

      dispatch(fetchOneCustomerSuccess(customer));

      return Promise.resolve(customer);
    } catch (error) {
      dispatch(fetchOneCustomerFailure());
    }

    return undefined;
  };

export const searchCustomers =
  (params: Array<{ key: string; value: string | number | Record<string, unknown> }>) =>
  async (dispatch: Dispatch): Promise<PaginatedResources<Customer>> => {
    if (indexPageCancellationToken) {
      indexPageCancellationToken.cancel('Index page change');
    }
    indexPageCancellationToken = axios.CancelToken.source();
    const customersUrl = buildUrl(`${apiUrl}/v2/customers`, params);

    const response = await dispatch(
      authRequest<PaginatedResources<Customer>>('get', customersUrl, undefined, {
        cancelToken: indexPageCancellationToken?.token,
      }),
    );
    return response.data;
  };

export const fetchNextCustomers = (): AsyncThunk<Customer[] | undefined> => async (dispatch, getState) => {
  const state = getState();
  const pagination = paginationSelector(state);
  const sort = sortSelector(state);
  const filterData = filterSelector(state);
  const columnFilter = filterColumnsSelector(state);

  if (pagination.currentPage === pagination.totalPages) {
    return undefined;
  }

  const pageToFetch = pagination.next ? pagination.currentPage + 1 : 1;

  // build filters
  const analysisFilter =
    filterData.hasAnalysis === false ? { $eq: 0 } : filterData.hasAnalysis === true ? { $gt: 0 } : undefined;
  const stateFilter = filterData.states.length > 0 ? { $in: filterData.states } : undefined;
  const teamFilter = filterData.team;
  const projectFilter = filterData.project?.id === undefined ? undefined : filterData.project.id;
  const userJourneyStateFilter = getUserJourneyStateFilter(filterData.stateFamilies);

  const computeSearchQueryParams = (): Array<{ [path: string]: { $regex: string; $options: string } }> | undefined => {
    return filterData.inputValue
      ? columnFilter.map((column) => {
          return {
            [column]: { $regex: escapeRegexReservedCharacters(filterData.inputValue as string), $options: 'i' },
          };
        })
      : undefined;
  };

  const requestParams = [
    { key: 'page', value: pageToFetch },
    { key: 'limit', value: perPage },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    { key: 'sort', value: { 'dashboard.newWithAnalysis': -1, [sort.sortBy]: getMongoSortValue(sort.sortDirection) } },
    {
      key: 'filter',
      value: {
        dashboard: { $exists: 1 },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'dashboard.numberOfAnalyses': analysisFilter,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'dashboard.currentState': stateFilter,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'dashboard.team': teamFilter === '*' ? undefined : teamFilter,
        ...(filterData.inputValue && { $or: computeSearchQueryParams() }),
        projectId: projectFilter,
        ['reports.userJourneyState']: userJourneyStateFilter,
      },
    },
  ];

  try {
    dispatch(fetchNextCustomerStart());
    const response = await dispatch(searchCustomers(requestParams));

    const { pagination: paginationData, resources: nextCustomers } = response;

    if (nextCustomers.length === 0 && pagination.currentPage === 0) {
      dispatch(fetchNextCustomerFailure({ error: 'noCustomerFound' }));

      return undefined;
    }

    dispatch(
      fetchNextCustomerSuccess({
        nextCustomers,
        paginationData: { ...paginationData, currentPage: pagination.currentPage + 1 },
      }),
    );

    return Promise.resolve(nextCustomers);
  } catch (error) {
    // case where the request has been cancelled
    if ((error as { message: string }).message === 'Index page change') {
      return;
    }
    dispatch(fetchNextCustomerFailure({ error: 'genericError' }));
  }

  return undefined;
};

export const refreshCustomerList = (): AsyncThunk => async (dispatch) => {
  dispatch(resetCustomerList());
  await dispatch(fetchNextCustomers());
  logAmplitudeEvent(AmplitudeEvent.NAVIGATION_REFRESH);
};

export const sortCustomerList = (): AsyncThunk => async (dispatch) => {
  dispatch(sortCustomersStart());
  await dispatch(fetchNextCustomers());
  dispatch(sortCustomersSuccess());
};

export const patchCustomerDashboardState =
  (customerId: string, currentState: State): AsyncThunk =>
  async (dispatch): Promise<void> => {
    const URL = `${apiUrl}/v2/customers/${customerId}/dashboard`;

    try {
      const { data: newDashboard } = await dispatch(authRequest<Dashboard>('patch', URL, { currentState }));

      dispatch(updateCustomerDashboardState({ customerId: customerId, newDashboard }));

      return;
    } catch (error) {
      throw error;
    }
  };

export let requestCancellationToken: CancelTokenSource | undefined;

export const filterCustomers =
  (newFilter: Partial<Omit<CustomersFilter, 'isLoading'>>): AsyncThunk =>
  async (dispatch) => {
    requestCancellationToken = axios.CancelToken.source();
    dispatch(filterCustomersStart(newFilter));
    await dispatch(fetchNextCustomers());
    dispatch(filterCustomersSuccess());

    logAmplitudeEvent(AmplitudeEvent.NAVIGATION_FILTER, newFilter);
  };

export const patchCustomerComment =
  (customerId: string, userComment: string): AsyncThunk =>
  async (dispatch): Promise<void> => {
    const url = `${apiUrl}/v2/customers/${customerId}`;
    const { data: customer } = await dispatch(
      authRequest<Customer>('patch', url, { dashboard: { comment: userComment } }),
    );
    dispatch(fetchOneCustomerSuccess(customer));
  };

export const createNewCustomer =
  (
    customerInvitation: CustomerInformationData,
    organizationId: string,
    projectId: string,
  ): AsyncThunk<InvitationResponse> =>
  async (dispatch) => {
    const url = `${apiUrl}/v2/transaction-data-integration/customers/start`;

    const { data: invitationResponse } = await dispatch(
      authRequest<InvitationResponse>('post', url, { ...customerInvitation, organizationId, projectId }),
    );

    return invitationResponse;
  };

export const deleteCustomer =
  (customerId: string, organizationId: string, projectId: string): AsyncThunk<void> =>
  async (dispatch) => {
    const url = `${apiUrl}/v2/customers/${customerId}`;

    try {
      await dispatch(authRequest<void>('delete', url, { organizationId, projectId }));
    } catch (error) {
      throw error;
    }
  };

export const patchCustomer =
  (customerId: string, updatedCustomerData: PatchCustomerPersonalDetailsDto): AsyncThunk<void> =>
  async (dispatch) => {
    const url = `${apiUrl}/v2/customers/${customerId}`;

    try {
      await dispatch(authRequest<void>('patch', url, updatedCustomerData));
    } catch (error) {
      throw error;
    }
  };

export const createLinkForNewCustomer =
  (customerId: string, organizationId: string, projectId: string): AsyncThunk<string> =>
  async (dispatch) => {
    const requestParams = [
      { key: 'organizationId', value: organizationId },
      { key: 'projectId', value: projectId },
    ];

    const url = buildUrl(
      `${apiUrl}/v2/transaction-data-integration/customers/${customerId}/aggregation-link`,
      requestParams,
    );

    const {
      data: { shortLink },
    } = await dispatch(authRequest<{ shortLink: string }>('get', url));
    await dispatch(fetchOneCustomer(customerId));

    return shortLink;
  };

export const sendInvitationForNewCustomer =
  (
    customerId: string,
    type: AutomaticSendType[],
    language: Language,
    organizationId: string,
    projectId: string,
  ): AsyncThunk<void> =>
  async (dispatch) => {
    const url = `${apiUrl}/v2/transaction-data-integration/customers/${customerId}/invitations`;
    const body = {
      sendEmail: type.includes(AutomaticSendType.EMAIL),
      sendSms: type.includes(AutomaticSendType.SMS),
      language: language,
      organizationId,
      projectId,
    };

    await dispatch(authRequest('post', url, body));
    await dispatch(fetchOneCustomer(customerId));
  };

export const sendLoanDecision =
  (customerId: string, decision: 'ACCEPTED' | 'REFUSED'): AsyncThunk =>
  async (dispatch) => {
    try {
      await axios.post('https://demo.algoan.ai/api/notification/send', {
        customerId,
        status: decision,
      });
      dispatch(setLoanDecision({ customerId, decision }));
      dispatch(
        notify({
          type: NotificationVariant.SUCCESS,
          message: customerMessages.loanDecisionSuccess,
        }),
      );
    } catch {
      dispatch(
        notify({
          type: NotificationVariant.ERROR,
          message: customerMessages.loanDecisionError,
        }),
      );
    }
  };
